"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.NotifiClientError = void 0;
const ensureSource_1 = __importStar(require("../utils/ensureSource"));
const ensureSourceGroup_1 = __importDefault(require("../utils/ensureSourceGroup"));
const ensureTargetGroup_1 = __importDefault(require("../utils/ensureTargetGroup"));
const ensureTargetIds_1 = __importDefault(require("../utils/ensureTargetIds"));
const fetchDataImpl_1 = __importDefault(require("../utils/fetchDataImpl"));
const packFilterOptions_1 = __importDefault(require("../utils/packFilterOptions"));
const storage_1 = __importDefault(require("../utils/storage"));
const useNotifiConfig_1 = __importDefault(require("./useNotifiConfig"));
const useNotifiService_1 = __importDefault(require("./useNotifiService"));
const react_1 = require("react");
class NotifiClientError extends Error {
    constructor(underlying) {
        super('NotifiClient encountered an error');
        this.underlying = underlying;
    }
}
exports.NotifiClientError = NotifiClientError;
const projectData = (internalData) => {
    if (internalData == null) {
        return null;
    }
    const { alerts, emailTargets, filters, smsTargets, sources, targetGroups, telegramTargets, } = internalData;
    return {
        alerts,
        emailTargets,
        filters,
        smsTargets,
        sources,
        targetGroups,
        telegramTargets,
    };
};
/**
 * React hook for Notifi SDK
 *
 * @remarks
 * Used to interact with Notifi services
 *
 * @param config - Options to configure the Notifi client
 * <br>
 * <br>
 * See [Alert Creation Guide]{@link https://docs.notifi.network} for more information on creating Alerts
 */
const useNotifiClient = (config) => {
    const { env, dappAddress, walletPublicKey } = config;
    const [isInitialized, setIsInitialized] = (0, react_1.useState)(false);
    const notifiConfig = (0, useNotifiConfig_1.default)(env);
    const { getAuthorization, setAuthorization } = (0, react_1.useMemo)(() => {
        return (0, storage_1.default)({
            dappAddress,
            walletPublicKey,
            jwtPrefix: notifiConfig.storagePrefix,
        });
    }, [dappAddress, walletPublicKey, notifiConfig.storagePrefix]);
    const service = (0, useNotifiService_1.default)(env);
    const [internalData, setInternalData] = (0, react_1.useState)(null);
    const [error, setError] = (0, react_1.useState)(null);
    const [loading, setLoading] = (0, react_1.useState)(false);
    const [isAuthenticated, setIsAuthenticated] = (0, react_1.useState)(false);
    const [expiry, setExpiry] = (0, react_1.useState)(null);
    const fetchDataRef = (0, react_1.useRef)({});
    /**
     * User's stored preferences for email, sms, etc.
     * @typedef {object} ClientData
     * @property {string | null} email - Email address for Alert notifications
     * @property {string | null} phoneNumber - Phone number for Alert notifications
     * @property {string | null} telegramId - Telegram username for Alert notifications
     *
     */
    /**
     * Fetch user's stored values from Notifi
     *
     * @remarks
     * Obtains the stored values for a user's Alert, Targets, Sources, etc.
     *
     * @returns {ClientData} User's stored values
     * <br>
     * <br>
     * See [Alert Creation Guide]{@link https://docs.notifi.network} for more information on creating Alerts
     */
    const fetchData = (0, react_1.useCallback)(async () => {
        try {
            setLoading(true);
            const newData = await (0, fetchDataImpl_1.default)(service, Date, fetchDataRef.current);
            setInternalData(newData);
            const clientData = projectData(newData);
            if (clientData === null) {
                throw new Error('Unknown error');
            }
            return clientData;
        }
        catch (e) {
            if (e instanceof Error) {
                setError(e);
            }
            else {
                setError(new NotifiClientError(e));
            }
            throw e;
        }
        finally {
            setLoading(false);
        }
    }, [service]);
    (0, react_1.useEffect)(() => {
        // Initial load
        const doInitialLoad = async () => {
            const authorization = await getAuthorization();
            if (authorization === null) {
                setIsAuthenticated(false);
                setIsInitialized(true);
                setExpiry(null);
                return;
            }
            const { token, expiry } = authorization;
            service.setJwt(token);
            const expiryDate = new Date(expiry);
            const now = new Date();
            if (expiryDate <= now) {
                setIsAuthenticated(false);
                setIsInitialized(true);
                setExpiry(expiry);
                return;
            }
            // Refresh if less than a week remaining
            const refreshTime = new Date();
            refreshTime.setDate(refreshTime.getDate() + 7);
            if (expiryDate < refreshTime) {
                try {
                    const { token: newToken, expiry: newExpiry } = await service.refreshAuthorization();
                    if (newToken !== null && newExpiry !== null) {
                        service.setJwt(newToken);
                        setAuthorization({ token: newToken, expiry: newExpiry });
                        setIsAuthenticated(true);
                        setExpiry(newExpiry);
                    }
                }
                catch (_e) {
                    // Explicity ignore refresh errors
                    setExpiry(expiry);
                    setIsAuthenticated(true);
                }
            }
            else {
                setExpiry(expiry);
                setIsAuthenticated(true);
            }
            const newData = await (0, fetchDataImpl_1.default)(service, Date, fetchDataRef.current);
            setInternalData(newData);
            setLoading(false);
        };
        setIsInitialized(false);
        doInitialLoad()
            .catch((_e) => {
            // Intentionally ignore
        })
            .then(() => {
            setIsInitialized(true);
        });
    }, [getAuthorization, setAuthorization]);
    /**
     * Authorization object containing token and metadata
     * @typedef {object} Authorization
     * @property {string | null} token - Authorization token
     * @property {string | null} expiry - Token expiry in ISO 8601-1:2019 format
     *
     */
    /**
     * User's object describing the user account
     * @typedef {object} User
     * @property {string | null} email - Email address associated with account. For dapp logins, the email is auto assigned, but can be changed later
     * @property {boolean} emailConfirmed - Is the account in confirmed? Only confirmed accounts can interact with Notifi
     *
     */
    /**
     * Log in to Notifi
     *
     * @remarks
     * Log in to Notif and obtain security context for future calls to set/retrieve account data
     *
     * @returns {ClientData} User's stored values
     * <br>
     * <br>
     * See [Alert Creation Guide]{@link https://docs.notifi.network} for more information on creating Alerts
     */
    const logIn = (0, react_1.useCallback)(async (signer) => {
        if (signer == null) {
            throw new Error('Signer cannot be null');
        }
        const timestamp = Math.round(Date.now() / 1000);
        setLoading(true);
        try {
            const messageBuffer = new TextEncoder().encode(`${walletPublicKey}${dappAddress}${timestamp.toString()}`);
            const signedBuffer = await signer.signMessage(messageBuffer);
            const signature = Buffer.from(signedBuffer).toString('base64');
            const result = await service.logInFromDapp({
                walletPublicKey,
                dappAddress,
                timestamp,
                signature,
            });
            if (result.authorization !== null) {
                const { token, expiry } = result.authorization;
                if (token !== null && expiry !== null) {
                    service.setJwt(token);
                    setAuthorization({ token, expiry });
                }
            }
            const newData = await (0, fetchDataImpl_1.default)(service, Date, fetchDataRef.current);
            setInternalData(newData);
            setIsAuthenticated(true);
            return result;
        }
        catch (e) {
            setIsAuthenticated(false);
            if (e instanceof Error) {
                setError(e);
            }
            else {
                setError(new NotifiClientError(e));
            }
            throw e;
        }
        finally {
            setLoading(false);
        }
    }, [service, walletPublicKey, dappAddress]);
    /**
     * Update an Alert
     *
     * @remarks
     * Use this to allow the user to update Alerts
     *
     * @param {ClientUpdateAlertInput} input - Input params for updating an Alert
     * @returns {UserAlert} An Alert object owned by the user
     * <br>
     * <br>
     * See [Alert Creation Guide]{@link https://docs.notifi.network} for more information on creating Alerts
     */
    const updateAlert = (0, react_1.useCallback)(async (input) => {
        const { alertId } = input;
        setLoading(true);
        try {
            const newData = await (0, fetchDataImpl_1.default)(service, Date, fetchDataRef.current);
            const { emailTargetIds, smsTargetIds, telegramTargetIds } = await (0, ensureTargetIds_1.default)(service, newData, input);
            const existingAlert = newData.alerts.find((a) => a.id === alertId);
            if (existingAlert === undefined) {
                throw new Error(`Unable to find alert ${alertId}`);
            }
            const name = existingAlert.name;
            if (name === null) {
                throw new Error(`Invalid Alert ${alertId}`);
            }
            const targetGroup = await (0, ensureTargetGroup_1.default)(service, newData.targetGroups, {
                name,
                emailTargetIds,
                smsTargetIds,
                telegramTargetIds,
            });
            const alertIndex = newData.alerts.indexOf(existingAlert);
            const newAlert = Object.assign(Object.assign({}, existingAlert), { targetGroup });
            newData.alerts[alertIndex] = newAlert;
            setInternalData(Object.assign({}, newData));
            return newAlert;
        }
        catch (e) {
            if (e instanceof Error) {
                setError(e);
            }
            else {
                setError(new NotifiClientError(e));
            }
            throw e;
        }
        finally {
            setLoading(false);
        }
    }, [service]);
    /**
     * Create an Alert
     *
     * @remarks
     * Use this to allow the user to create Alerts
     *
     * @param {ClientCreateAlertInput} input - Input params for creating an Alert
     * @returns {UserAlert} An Alert object owned by the user
     * <br>
     * <br>
     * See [Alert Creation Guide]{@link https://docs.notifi.network} for more information on creating Alerts
     */
    const createAlert = (0, react_1.useCallback)(async (input) => {
        const { name, filterId, filterOptions, sourceId, groupName = 'default', } = input;
        setLoading(true);
        try {
            const newData = await (0, fetchDataImpl_1.default)(service, Date, fetchDataRef.current);
            const { emailTargetIds, smsTargetIds, telegramTargetIds } = await (0, ensureTargetIds_1.default)(service, newData, input);
            const existingAlert = newData.alerts.find((a) => a.name === name);
            if (existingAlert !== undefined) {
                throw new Error(`Name must be unique! Found alert ${existingAlert.id} with name ${name}`);
            }
            const sourceToUse = newData.sources.find((s) => s.id === sourceId);
            if (sourceToUse === undefined) {
                throw new Error(`Invalid source id ${sourceId}`);
            }
            const existingFilter = sourceToUse.applicableFilters.find((f) => f.id === filterId);
            if (existingFilter === undefined) {
                throw new Error(`Invalid filter id ${filterId}`);
            }
            const sourceGroup = await (0, ensureSourceGroup_1.default)(service, newData.sourceGroups, {
                name,
                sourceIds: [sourceId],
            });
            const targetGroup = await (0, ensureTargetGroup_1.default)(service, newData.targetGroups, {
                name,
                emailTargetIds,
                smsTargetIds,
                telegramTargetIds,
            });
            const sourceGroupId = sourceGroup.id;
            if (sourceGroupId === null) {
                throw new Error(`Unknown error creating source group`);
            }
            const targetGroupId = targetGroup.id;
            if (targetGroupId === null) {
                throw new Error(`Unknown error creating target group`);
            }
            const alert = await service.createAlert({
                name,
                sourceGroupId,
                filterId,
                filterOptions: (0, packFilterOptions_1.default)(filterOptions),
                targetGroupId,
                groupName,
            });
            // The returned target group doesn't have individual targets
            // Work around by spreading the known target group
            const newAlert = Object.assign(Object.assign({}, alert), { sourceGroup,
                targetGroup });
            newData.alerts.push(newAlert);
            setInternalData(Object.assign({}, newData));
            return newAlert;
        }
        catch (e) {
            if (e instanceof Error) {
                setError(e);
            }
            else {
                setError(new NotifiClientError(e));
            }
            throw e;
        }
        finally {
            setLoading(false);
        }
    }, [service]);
    /**
     * Delete an Alert
     *
     * @remarks
     * Use this to allow the user to delete Alerts
     *
     * @param {@link ClientDeleteAlertInput} input - Input params for deleting an Alert
     * @returns {string} The processed ID
     * <br>
     * <br>
     * See [Alert Creation Guide]{@link https://docs.notifi.network} for more information on creating Alerts
     */
    const deleteAlert = (0, react_1.useCallback)(async (input) => {
        const { alertId, keepSourceGroup = false, keepTargetGroup = false, } = input;
        try {
            const newData = await (0, fetchDataImpl_1.default)(service, Date, fetchDataRef.current);
            const alertToDelete = newData.alerts.find((a) => {
                return a.id === alertId;
            });
            if (alertToDelete === undefined) {
                throw new Error(`Unknown alert id ${alertId}`);
            }
            const targetGroupId = alertToDelete.targetGroup.id;
            const sourceGroupId = alertToDelete.sourceGroup.id;
            await service.deleteAlert({ id: alertId });
            newData.alerts = newData.alerts.filter((a) => a !== alertToDelete);
            if (targetGroupId !== null && !keepTargetGroup) {
                await service
                    .deleteTargetGroup({
                    id: targetGroupId,
                })
                    .then(() => {
                    newData.targetGroups = newData.targetGroups.filter((t) => t.id !== targetGroupId);
                });
            }
            if (sourceGroupId !== null && !keepSourceGroup) {
                await service
                    .deleteSourceGroup({
                    id: sourceGroupId,
                })
                    .then(() => {
                    newData.sourceGroups = newData.sourceGroups.filter((s) => s.id !== sourceGroupId);
                });
            }
            setInternalData(Object.assign({}, newData));
            return alertId;
        }
        catch (e) {
            if (e instanceof Error) {
                setError(e);
            }
            else {
                setError(new NotifiClientError(e));
            }
            throw e;
        }
        finally {
            setLoading(false);
        }
    }, [service]);
    /**
     * Create a new Source
     *
     * @remarks
     * Use this to allow the user to create a Source object that emits events
     *
     * @param {CreateSourceInput} input - Input params for creating a Source
     * @returns {Source} A Source object that can be used to create an Alert
     * <br>
     * <br>
     * See [Alert Creation Guide]{@link https://docs.notifi.network} for more information on creating Alerts
     */
    const createSource = (0, react_1.useCallback)(async (input) => {
        setLoading(true);
        try {
            const newData = await (0, fetchDataImpl_1.default)(service, Date, fetchDataRef.current);
            const source = await (0, ensureSource_1.default)(service, newData.sources, input);
            source.applicableFilters.forEach((applicableFilter) => {
                const existing = newData.filters.find((existingFilter) => applicableFilter.id === existingFilter.id);
                if (existing !== undefined) {
                    newData.filters.push(applicableFilter);
                }
            });
            setInternalData(newData);
            return source;
        }
        catch (e) {
            if (e instanceof Error) {
                setError(e);
            }
            else {
                setError(new NotifiClientError(e));
            }
            throw e;
        }
        finally {
            setLoading(false);
        }
    }, [service]);
    /**
     * Create a Metaplex Auction Source
     *
     * @remarks
     * Use this to allow the user to create a Source object that emits evevents from Metaplex auctions
     *
     * @param {ClientCreateMetaplexAuctionSourceInput} input - Input params for creating a Metaplex Auction Source
     * @returns {Source} A Source object that can be used to create an Alert
     * <br>
     * <br>
     * See [Alert Creation Guide]{@link https://docs.notifi.network} for more information on creating Alerts
     */
    const createMetaplexAuctionSource = (0, react_1.useCallback)(async (input) => {
        setLoading(true);
        try {
            const newData = await (0, fetchDataImpl_1.default)(service, Date, fetchDataRef.current);
            const source = await (0, ensureSource_1.ensureMetaplexAuctionSource)(service, newData.sources, input);
            setInternalData(newData);
            return source;
        }
        catch (e) {
            if (e instanceof Error) {
                setError(e);
            }
            else {
                setError(new NotifiClientError(e));
            }
            throw e;
        }
        finally {
            setLoading(false);
        }
    }, [service]);
    /**
     * Get the configurations associated with the configured dapp
     *
     * @remarks
     * Use this to determine which Target inputs are supported before a user has authenticated.
     * This also returns the list of country codes that are supported for SMS
     *
     */
    const getConfiguration = (0, react_1.useCallback)(async () => {
        setLoading(true);
        try {
            return await service.getConfigurationForDapp({
                dappAddress: config.dappAddress,
            });
        }
        catch (e) {
            if (e instanceof Error) {
                setError(e);
            }
            else {
                setError(new NotifiClientError(e));
            }
            throw e;
        }
        finally {
            setLoading(false);
        }
    }, [config.dappAddress, service]);
    const data = (0, react_1.useMemo)(() => {
        return projectData(internalData);
    }, [internalData]);
    const client = {
        logIn,
        createAlert,
        createMetaplexAuctionSource,
        createSource,
        deleteAlert,
        fetchData,
        getConfiguration,
        updateAlert,
    };
    return Object.assign({ data,
        error,
        expiry,
        isAuthenticated,
        isInitialized,
        loading }, client);
};
exports.default = useNotifiClient;
//# sourceMappingURL=data:application/json;base64,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